import { useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronLeft, Lock } from 'lucide-react';

import { TicketPriorityBadge, TicketStatusBadge } from '@/components/support/TicketBadges';
import { TicketReplyBox } from '@/components/support/TicketReplyBox';
import { TicketThread } from '@/components/support/TicketThread';
import { Alert } from '@/components/ui/Alert';
import { QueryError } from '@/components/ui/QueryError';
import { SkeletonLines } from '@/components/ui/Skeleton';
import { TICKET_CATEGORY_LABEL } from '@/constants/support';
import { useApiQuery } from '@/hooks/useApiQuery';
import { toast } from '@/hooks/useToast';
import { downloadPrivateFile } from '@/lib/download';
import { formatDateTime } from '@/lib/format';
import { supportApi } from '../api/support.api';

const BACK = { to: '/admin/support', label: 'Back to support' };

/**
 * One ticket, from the school's side: where it stands and what to do next at
 * the top, the conversation and the reply box on the left, the ticket's facts
 * on the right.
 */
export function SupportTicketPage() {
  const { uuid = '' } = useParams();
  const { data: ticket, isLoading, error, refetch } = useApiQuery(() => supportApi.get(uuid), [uuid]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [ticket?.messages?.length]);

  if (error) {
    return <QueryError title="Could not load this ticket" error={error} onRetry={refetch} />;
  }

  if (isLoading || !ticket) {
    return (
      <div className="flex flex-col gap-4 max-w-6xl mx-auto w-full">
        <div className="card p-4 sm:p-5 bg-surface border border-line rounded-2xl shadow-xs">
          <SkeletonLines count={3} />
        </div>
        <div className="card p-5">
          <SkeletonLines count={6} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 max-w-6xl mx-auto w-full">
      {/* Top compact navigation & ticket meta header */}
      <div className="card p-4 sm:p-5 bg-surface border border-line rounded-2xl shadow-xs flex flex-col gap-3">
        {/* Row 1: Back link + Status & Priority Badges */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <Link
            to={BACK.to}
            className="inline-flex items-center gap-1.5 text-xs font-bold text-ink-3 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
          >
            <ChevronLeft className="h-4 w-4 stroke-[2.5]" />
            <span>{BACK.label}</span>
          </Link>

          <div className="flex items-center gap-2">
            <TicketStatusBadge status={ticket.status} />
            <TicketPriorityBadge priority={ticket.priority} always />
          </div>
        </div>

        {/* Row 2: Ticket Number + Subject Title + Metadata */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-t border-line/60 pt-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="px-2 py-0.5 rounded-md text-base font-mono font-bold bg-surface-2 border border-line text-ink-2 shrink-0">
              {ticket.ticketNumber}
            </span>
            <h1 className="text-lg sm:text-xl font-black tracking-tight text-ink truncate">
              {ticket.subject}
            </h1>
          </div>

          <div className="flex items-center gap-2 text-xs text-ink-3 font-medium flex-wrap shrink-0">
            <span className="px-2 py-0.5 rounded-full bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 font-bold border border-indigo-200/60 dark:border-indigo-800/40">
              {TICKET_CATEGORY_LABEL[ticket.category]}
            </span>
            <span>•</span>
            <span>
              Raised by <strong className="text-ink-2 font-bold">{ticket.raisedBy.name}</strong> on{' '}
              {formatDateTime(ticket.createdAt)}
            </span>
            {ticket.assignedTo && (
              <>
                <span>•</span>
                <span>
                  Handled by <strong className="text-ink-2 font-bold">{ticket.assignedTo.name}</strong>
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      {ticket.status === 'WAITING_ON_SCHOOL' && (
        <Alert tone="warning" title="Our team is waiting for you">
          Read the latest reply below and answer it so we can carry on.
        </Alert>
      )}

      {/* Main chat window container */}
      <div className="card flex flex-col p-4 sm:p-6 shadow-xs border border-line bg-surface overflow-hidden h-[calc(100vh-220px)] min-h-[500px] rounded-2xl">
        <div className="shrink-0 border-b border-line pb-3 flex items-center justify-between">
          <span className="lbl text-ink-2">Conversation</span>
          <span className="text-xs text-ink-3 font-medium">
            {ticket.messages.length} message{ticket.messages.length === 1 ? '' : 's'}
          </span>
        </div>

        <section aria-label="Conversation" className="py-2 flex-1 min-h-0 overflow-y-auto pr-1">
          <TicketThread
            messages={ticket.messages}
            viewerSide="SCHOOL"
            onOpenAttachment={(attachment) =>
              void downloadPrivateFile(supportApi.attachmentPath(ticket.uuid, attachment.uuid), attachment.fileName).catch(
                () => toast.error('File not opened', 'Please try again.'),
              )
            }
          />
          <div ref={messagesEndRef} />
        </section>

        <section className="shrink-0 pt-3 border-t border-line/80 bg-surface" aria-labelledby="reply-heading">
          {ticket.status === 'CLOSED' ? (
            <p className="flex items-center gap-2 text-sm text-ink-2 rounded-xl bg-surface-2 p-3 border border-line">
              <Lock className="h-4 w-4 text-ink-3" aria-hidden />
              This ticket is closed. If you still need help, raise a new ticket from the Support page.
            </p>
          ) : (
            <TicketReplyBox
              placeholder={
                ticket.status === 'RESOLVED'
                  ? 'Still not fixed? Replying reopens the ticket.'
                  : 'Write a reply to our support team…'
              }
              onSend={async (message, files) => {
                // A failure propagates to the box, which shows it and keeps the draft.
                await supportApi.reply(ticket.uuid, message, files);
                toast.success('Reply sent', 'Our support team has been told.');
                refetch();
              }}
            />
          )}
        </section>
      </div>
    </div>
  );
}
